import { useState } from "react";
import { useNavigate } from "react-router";
import type { LoadFilterOptions } from "@generated/import-openapi.gen";

import type { LoadDebugRecord } from "@prequel/import-react";
import { useLoadDebug } from "@prequel/import-react";
import { Badge, BadgeColor, Button, ButtonStyle, Modal, type TableRow } from "@prequel/shared-components";

import { useRunLoad } from "@/api/import/streams";
import ResourceTable, { type FilterField } from "@/components/ResourceTable";
import Timestamp from "@/components/Timestamp";
import {
  getLoadRecordOutcome,
  getLoadRecordRowID,
  getLoadRecordStage,
  getSelectedRecordIDs,
  LOAD_RECORD_OUTCOME_LABELS,
  LOAD_RECORD_STAGE_LABELS,
  type LoadRecordOutcome,
} from "@/pages/import/StreamsPage/helpers";
import { useLoadContext } from "@/pages/import/StreamsPage/LoadPage";
import RunLoadModal from "@/pages/import/StreamsPage/RunLoadModal";

const COLUMNS = ["Record ID", "Stage", "Outcome", "Event Time"];

const OUTCOME_COLORS: Record<LoadRecordOutcome, BadgeColor> = {
  errored: BadgeColor.RED,
  succeeded: BadgeColor.GREEN,
};

const FILTER_FIELDS: FilterField[] = [
  {
    key: "has_error",
    label: "State",
    queryParam: "has_error",
    options: [
      { value: "true", label: "Errored" },
      { value: "false", label: "Not errored" },
    ],
    radio: true,
  },
];

const mapRecordToRow = (record: LoadDebugRecord): TableRow => {
  const outcome = getLoadRecordOutcome(record);

  return {
    id: getLoadRecordRowID(record),
    cells: [
      { value: <span className="font-mono text-xs">{record.record_id}</span> },
      { value: LOAD_RECORD_STAGE_LABELS[getLoadRecordStage(record)] },
      {
        value: (
          <Badge className="normal-case" color={OUTCOME_COLORS[outcome]} text={LOAD_RECORD_OUTCOME_LABELS[outcome]} />
        ),
      },
      { value: record.event_timestamp ? <Timestamp timestamp={record.event_timestamp} /> : "—" },
    ],
  };
};

const LoadRecordsTable = () => {
  const { load } = useLoadContext();
  const navigate = useNavigate();
  const runLoad = useRunLoad();
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const useQuery = (params: { cursor?: string; pageSize?: number }, filters?: Record<string, string>) =>
    useLoadDebug(load.id, params, filters);

  // Rows are stage events, so several selected rows can name the same record.
  const selectedRecordIDs = getSelectedRecordIDs(selectedIds);

  const onRunCustomLoad = (filter: LoadFilterOptions) =>
    runLoad.mutate(
      { id: load.stream_id, filter },
      {
        onSuccess: () => {
          setShowFilterModal(false);
          setSelectedIds([]);
          navigate(`/import/streams/${load.stream_id}/loads`);
        },
      },
    );

  return (
    <div className="mt-6">
      <Modal open={showFilterModal} onClose={() => setShowFilterModal(false)}>
        {showFilterModal && (
          <RunLoadModal
            isSubmitting={runLoad.isPending}
            initialRecordIDs={selectedRecordIDs}
            onCancel={() => setShowFilterModal(false)}
            onSubmit={onRunCustomLoad}
          />
        )}
      </Modal>
      <ResourceTable<LoadDebugRecord>
        columns={COLUMNS}
        useQuery={useQuery}
        rowMapper={mapRecordToRow}
        emptyMessage="No records found."
        filterFields={FILTER_FIELDS}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectionLabel={(row) => `Select record ${row.id}`}
        actions={
          <div className="flex items-center gap-3">
            {selectedRecordIDs.length > 0 && (
              <span className="text-sm text-stone-600">
                {selectedRecordIDs.length} record{selectedRecordIDs.length === 1 ? "" : "s"} selected
              </span>
            )}
            <Button
              text="Run Custom Load"
              type={ButtonStyle.PRIMARY}
              disabled={selectedRecordIDs.length === 0 || runLoad.isPending}
              onClick={() => setShowFilterModal(true)}
            />
          </div>
        }
      />
    </div>
  );
};

export default LoadRecordsTable;
